const REVIEW_PENDING_STATUS = "5";
const PENDING_UPLOAD_PREFIX = "document-analysis.pending-upload.";
const DOCUMENT_BASELINE_PREFIX = "document-analysis.document-baseline.";
const pendingMoveCounts = new Map<string, number>();

const TRACKED_DOCUMENT_ID_KEYS = new Set([
  "application_document_id",
  "ApplicationDocumentID",
  "BarcodeDocumentID",
  "barcode_document_id",
  "DocumentBackID",
  "DocumentFrontID",
]);

const normalizeValue = (value: unknown) => {
  const normalized = String(value ?? "").trim();
  if (
    normalized === "" ||
    normalized.toLowerCase() === "null" ||
    normalized.toLowerCase() === "undefined"
  ) {
    return null;
  }
  return normalized;
};

const normalizeDocumentId = (value: unknown) => {
  const normalized = normalizeValue(value);
  if (!normalized || normalized === "0") {
    return null;
  }
  return normalized;
};

export const collectDocumentIdentifiers = (
  value: unknown,
  result = new Set<string>()
) => {
  if (Array.isArray(value)) {
    value.forEach((item) => collectDocumentIdentifiers(item, result));
    return result;
  }

  if (!value || typeof value !== "object") {
    return result;
  }

  Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
    if (TRACKED_DOCUMENT_ID_KEYS.has(key)) {
      const documentId = normalizeDocumentId(nestedValue);
      if (documentId) {
        result.add(documentId);
      }
      return;
    }

    collectDocumentIdentifiers(nestedValue, result);
  });

  return result;
};

const getPendingUploadKey = (trnId: unknown) => {
  const normalizedTrnId = normalizeValue(trnId);
  if (!normalizedTrnId) {
    return null;
  }
  return `${PENDING_UPLOAD_PREFIX}${normalizedTrnId}`;
};

const getDocumentBaselineKey = (trnId: unknown) => {
  const normalizedTrnId = normalizeValue(trnId);
  if (!normalizedTrnId) {
    return null;
  }
  return `${DOCUMENT_BASELINE_PREFIX}${normalizedTrnId}`;
};

const serializeDocumentIdentifiers = (documentIds: Iterable<string>) =>
  JSON.stringify(Array.from(documentIds).map(String).sort());

const parseDocumentIdentifiers = (value: string | null) => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return new Set(parsed.map((item) => String(item)));
  } catch {
    return null;
  }
};

export const markPendingDocumentUpload = (trnId: unknown) => {
  const pendingUploadKey = getPendingUploadKey(trnId);
  if (!pendingUploadKey || typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.setItem(pendingUploadKey, "1");
};

export const hasPendingDocumentUpload = (trnId: unknown) => {
  const pendingUploadKey = getPendingUploadKey(trnId);
  if (!pendingUploadKey || typeof sessionStorage === "undefined") {
    return false;
  }

  return sessionStorage.getItem(pendingUploadKey) === "1";
};

export const clearPendingDocumentUpload = (trnId: unknown) => {
  const pendingUploadKey = getPendingUploadKey(trnId);
  if (!pendingUploadKey || typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.removeItem(pendingUploadKey);
};

export const syncInitialDocumentIdentifiers = (
  trnId: unknown,
  documentIds: Iterable<string>
) => {
  const documentBaselineKey = getDocumentBaselineKey(trnId);
  if (!documentBaselineKey || typeof sessionStorage === "undefined") {
    return;
  }

  if (sessionStorage.getItem(documentBaselineKey) !== null) {
    return;
  }

  sessionStorage.setItem(
    documentBaselineKey,
    serializeDocumentIdentifiers(documentIds)
  );
};

export const hasDocumentIdentifiersChanged = (
  trnId: unknown,
  documentIds: Iterable<string>
) => {
  const documentBaselineKey = getDocumentBaselineKey(trnId);
  if (!documentBaselineKey || typeof sessionStorage === "undefined") {
    return false;
  }

  const baselineIdentifiers = parseDocumentIdentifiers(
    sessionStorage.getItem(documentBaselineKey)
  );
  if (!baselineIdentifiers) {
    return false;
  }

  for (const documentId of documentIds) {
    if (!baselineIdentifiers.has(String(documentId))) {
      return true;
    }
  }

  return false;
};

export const resetDocumentUploadTracking = (
  trnId: unknown,
  documentIds: Iterable<string>
) => {
  const documentBaselineKey = getDocumentBaselineKey(trnId);
  if (documentBaselineKey && typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(
      documentBaselineKey,
      serializeDocumentIdentifiers(documentIds)
    );
  }

  clearPendingDocumentUpload(trnId);
};

export const incrementPendingDocumentMoveCount = (trnId: unknown) => {
  const normalizedTrnId = normalizeValue(trnId);
  if (!normalizedTrnId) {
    return 0;
  }

  const nextCount = (pendingMoveCounts.get(normalizedTrnId) || 0) + 1;
  pendingMoveCounts.set(normalizedTrnId, nextCount);
  return nextCount;
};

export const decrementPendingDocumentMoveCount = (trnId: unknown) => {
  const normalizedTrnId = normalizeValue(trnId);
  if (!normalizedTrnId) {
    return 0;
  }

  const currentCount = pendingMoveCounts.get(normalizedTrnId) || 0;
  const nextCount = Math.max(currentCount - 1, 0);

  if (nextCount === 0) {
    pendingMoveCounts.delete(normalizedTrnId);
    return 0;
  }

  pendingMoveCounts.set(normalizedTrnId, nextCount);
  return nextCount;
};

export const getPendingDocumentMoveCount = (trnId: unknown) => {
  const normalizedTrnId = normalizeValue(trnId);
  if (!normalizedTrnId) {
    return 0;
  }

  return pendingMoveCounts.get(normalizedTrnId) || 0;
};

export const resetPendingDocumentMoveCount = (trnId: unknown) => {
  const normalizedTrnId = normalizeValue(trnId);
  if (!normalizedTrnId) {
    return;
  }

  pendingMoveCounts.delete(normalizedTrnId);
};

type ResolveSubmitStatusArgs = {
  requestedStatus?: unknown;
  currentStatus?: unknown;
  hasExistingDocuments: boolean;
  hasPendingNewDocuments: boolean;
  forceReviewPending?: boolean;
  forceReviewPendingWhenExistingDocuments?: boolean;
  normalizeInvitedSubmitToInProgress?: boolean;
};

export const resolveSubmitStatus = ({
  requestedStatus,
  currentStatus,
  hasExistingDocuments,
  hasPendingNewDocuments,
  forceReviewPending = false,
  forceReviewPendingWhenExistingDocuments = false,
  normalizeInvitedSubmitToInProgress = false,
}: ResolveSubmitStatusArgs) => {
  if (forceReviewPending) {
    return REVIEW_PENDING_STATUS;
  }

  const normalizedRequestedStatus = normalizeValue(requestedStatus);
  if (
    normalizedRequestedStatus &&
    normalizedRequestedStatus !== REVIEW_PENDING_STATUS
  ) {
    return normalizedRequestedStatus;
  }

  if (
    forceReviewPendingWhenExistingDocuments &&
    hasExistingDocuments
  ) {
    return REVIEW_PENDING_STATUS;
  }

  if (hasPendingNewDocuments) {
    return REVIEW_PENDING_STATUS;
  }

  const normalizedCurrentStatus = normalizeValue(currentStatus);
  const normalizedFallbackStatus =
    normalizeInvitedSubmitToInProgress &&
    (!normalizedCurrentStatus || normalizedCurrentStatus === "1")
      ? "2"
      : normalizedCurrentStatus;

  // No documents at all: keep whatever status the application already has (or the explicit request).
  if (!hasExistingDocuments && !hasPendingNewDocuments) {
    if (normalizedRequestedStatus) {
      return normalizedRequestedStatus;
    }
    if (normalizedFallbackStatus) {
      return normalizedFallbackStatus;
    }
    return REVIEW_PENDING_STATUS;
  }

  if (
    hasExistingDocuments &&
    !hasPendingNewDocuments
  ) {
    return normalizedFallbackStatus || normalizedRequestedStatus || REVIEW_PENDING_STATUS;
  }

  return normalizedRequestedStatus || REVIEW_PENDING_STATUS;
};

export { REVIEW_PENDING_STATUS };
